import { Link, useLocation } from "react-router-dom";
import { Home, Rocket, Wrench, ClipboardList, Building2, BookOpen, ChevronDown, Droplets, FlaskConical, Settings, DollarSign, FileText, GraduationCap, Star, Shield } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useState, useEffect } from "react";

type NavItem = { name: string; path: string };
type NavSection = { 
  title: string; 
  icon: React.ElementType;
  key: string;
  items: NavItem[];
};
type NavCategory = {
  category: string;
  icon: React.ElementType;
  sections: NavSection[];
};

const navigation: NavCategory[] = [
  {
    category: "Getting Started",
    icon: Rocket,
    sections: [
      {
        title: "Onboarding",
        icon: GraduationCap,
        key: "onboarding",
        items: [
          { name: "Welcome to BlueLux", path: "/wiki/onboarding/welcome" },
          { name: "First Day Guide", path: "/wiki/onboarding/first-day" },
          { name: "Training Overview", path: "/wiki/onboarding/training" }
        ]
      }
    ]
  },
  {
    category: "Technical Knowledge",
    icon: Wrench,
    sections: [
      {
        title: "Fundamentals",
        icon: Droplets,
        key: "fundamentals",
        items: [
          { name: "The Pool System", path: "/wiki/fundamentals/pool-system" },
          { name: "Water Chemistry", path: "/wiki/fundamentals/chemistry" },
          { name: "Circulation", path: "/wiki/fundamentals/circulation" },
          { name: "Cleaning", path: "/wiki/fundamentals/cleaning" }
        ]
      },
      {
        title: "Equipment",
        icon: Settings,
        key: "equipment",
        items: [
          { name: "Filters", path: "/wiki/equipment/filters" },
          { name: "Salt Systems", path: "/wiki/equipment/salt-systems" },
          { name: "Pumps", path: "/wiki/equipment/pumps" },
          { name: "Actuators", path: "/wiki/equipment/actuators" }
        ]
      },
      {
        title: "Water Conditions",
        icon: FlaskConical,
        key: "conditions",
        items: [
          { name: "Algae", path: "/wiki/conditions/algae" },
          { name: "Cloudy Water", path: "/wiki/conditions/cloudy-water" },
          { name: "Green-to-Clean", path: "/wiki/conditions/green-to-clean" }
        ]
      }
    ]
  },
  {
    category: "Field Operations",
    icon: ClipboardList,
    sections: [
      {
        title: "Procedures",
        icon: ClipboardList,
        key: "procedures",
        items: [
          { name: "Service Workflow", path: "/wiki/procedures/service-workflow" },
          { name: "Equipment SOPs", path: "/wiki/procedures/equipment-sops" },
          { name: "Emergency Protocols", path: "/wiki/procedures/emergency" }
        ]
      },
      {
        title: "Service Standards",
        icon: Star,
        key: "service",
        items: [
          { name: "Standards & Requirements", path: "/wiki/service/standards" },
          { name: "App Documentation", path: "/wiki/service/app-documentation" },
          { name: "Customer Interaction", path: "/wiki/service/customer-interaction" },
          { name: "Safety", path: "/wiki/service/safety" }
        ]
      }
    ]
  },
  {
    category: "Company",
    icon: Building2,
    sections: [
      {
        title: "Compensation",
        icon: DollarSign,
        key: "compensation",
        items: [
          { name: "Pay & Incentives", path: "/wiki/compensation/pay-incentives" },
          { name: "Calculator", path: "/wiki/compensation/calculator" },
          { name: "FAQ", path: "/wiki/compensation/faq" }
        ]
      },
      {
        title: "Policies",
        icon: FileText,
        key: "policies",
        items: [
          { name: "IC Status", path: "/wiki/policies/ic-status" },
          { name: "Responsibilities", path: "/wiki/policies/responsibilities" },
          { name: "Time Off & Termination", path: "/wiki/policies/time-off-termination" },
          { name: "Uniform", path: "/wiki/policies/uniform" },
          { name: "Code of Conduct", path: "/wiki/policies/conduct" }
        ]
      }
    ]
  },
  {
    category: "Quick Reference",
    icon: BookOpen,
    sections: [
      {
        title: "Resources",
        icon: BookOpen,
        key: "resources",
        items: [
          { name: "Chemical Tables", path: "/wiki/resources/chemicals" },
          { name: "Route Coverage", path: "/wiki/resources/route-coverage" },
          { name: "Emergency Contacts", path: "/wiki/resources/contacts" },
          { name: "Forms & Documents", path: "/wiki/resources/forms" },
          { name: "FAQ", path: "/wiki/resources/faq" }
        ]
      }
    ]
  }
];

export const WikiSidebar = () => {
  const location = useLocation();
  const [openSections, setOpenSections] = useState<string[]>(["onboarding"]);

  // Find all section keys for lookup
  const allSections = navigation.flatMap(cat => cat.sections);

  useEffect(() => {
    const activeSection = allSections.find(section => 
      section.items.some(item => location.pathname === item.path)
    );
    if (activeSection) {
      setOpenSections([activeSection.key]);
    }
  }, [location.pathname]);

  const toggleSection = (key: string) => {
    setOpenSections(prev => 
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  return (
    <aside className="hidden lg:block w-64 border-r border-border bg-card/50 p-6 sticky top-20 h-[calc(100vh-5rem)] overflow-y-auto">
      <Link 
        to="/" 
        className="flex items-center gap-2 text-foreground hover:text-primary transition-colors mb-6 font-semibold"
      >
        <Home className="h-5 w-5" />
        Home
      </Link>
      
      {navigation.map((category, catIndex) => {
        const CategoryIcon = category.icon;
        
        return (
          <div key={category.category} className={catIndex > 0 ? "mt-6" : ""}>
            {/* Category Header */}
            <div className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 px-1">
              <CategoryIcon className="h-3.5 w-3.5" />
              {category.category}
            </div>
            
            {/* Sections within category */}
            {category.sections.map((section) => {
              const SectionIcon = section.icon;
              const isOpen = openSections.includes(section.key);
              
              return (
                <Collapsible
                  key={section.key}
                  open={isOpen}
                  onOpenChange={() => toggleSection(section.key)}
                  className="mb-2"
                >
                  <CollapsibleTrigger className="flex items-center justify-between w-full text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-1.5 px-2 rounded-md hover:bg-muted/50 group">
                    <div className="flex items-center gap-2">
                      <SectionIcon className="h-4 w-4" />
                      {section.title}
                    </div>
                    <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <ul className="space-y-0.5 ml-6 mt-1">
                      {section.items.map((item) => (
                        <li key={item.path}>
                          <Link
                            to={item.path}
                            className={`block text-sm py-1.5 px-2 rounded transition-colors ${
                              location.pathname === item.path
                                ? 'bg-primary/10 text-primary font-medium'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                            }`}
                          >
                            {item.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
            
            {/* Divider after each category except the last */}
            {catIndex < navigation.length - 1 && (
              <div className="border-b border-border/50 mt-4" />
            )}
          </div>
        );
      })}
    </aside>
  );
};
